(ns frontend.view.nev.kyc.process-status-views.view-utils
    (:require
      [clojure.string :as string]
      [common.localization :as loc]
      [common.util :as cu]
      [frontend.account :as account]
      [frontend.actions.kyc-process :as kyc-process-actions]
      [frontend.logging :as logging]
      [frontend.routing :as routing]
      [frontend.state.current-session :as session]
      [frontend.utils :as fu]
      [frontend.view.modals.confirmation.view :as confirmation-modal]
      [frontend.view.modals.events :as modals]
      [frontend.view.modals.notification.view :as notification-modal]
      [frontend.view.nev.kyc.process.editor-utils :as editor-utils]
      [ui.components.button.view :as button]
      [ui.dom-globals :as dg]))

(defn resolve-missing-fields
  [lang-kw template-schema answers-atom entity]
  (->> template-schema
       (map second)
       (mapcat :questions)
       (remove (fn [[_k m]]
                 (:optional-field? m)))
       (filter (fn [[k m]]
                 (editor-utils/missing-mandatory-value? k m @answers-atom entity)))
       (keep (fn [[_k meta]]
               (some-> meta
                       :translations
                       (get lang-kw))))
       (doall)))

(defn resolve-validation-errors
  [{:keys [lang-kw template-schema answers-atom field-errors-atom kyc-process]}]
  (let [answers @answers-atom
        field-errors @field-errors-atom]
    (->> template-schema
         (sort-by (fn [[_block-kw block-config]]
                    (:ord block-config)))
         (map second)
         (mapcat :questions)
         (keep (fn [[field {:keys [validation-fn] :as meta}]]
                 (let [field-label (get-in meta [:translations lang-kw])
                       generic-error-str (get field-errors field)
                       custom-validation-error-str (when (fn? validation-fn)
                                                     (when-let [error (validation-fn
                                                                       {:answers answers
                                                                        :kyc-process kyc-process})]
                                                       (if (keyword? error)
                                                         (loc/tr error nil lang-kw)
                                                         (str error))))
                       error-str (or generic-error-str custom-validation-error-str)]
                   (when-not (cu/blank-value? error-str)
                     (str field-label ": " error-str)))))
         (vec))))

(defn error-hiccup
  [lang-kw missing-mandatory-fields]
  [:ul
   {:class "list-disc pl-4"}
   (concat (->> missing-mandatory-fields
                (take 5)
                (map-indexed (fn [idx err]
                               ^{:key (str "kyc-wip-continue-error-" idx)}
                               [:li (str err)]))
                (doall))
           (when (> (count missing-mandatory-fields) 5)
             [^{:key "kyc-wip-continue-error-other"}
              [:li
               (loc/tr :and-x-more-missing-fields [(- (count missing-mandatory-fields) 5)] lang-kw)]]))])

(def ^:private max-tooltip-items 5)

(defn modal-validator-tooltip
  "Builds the disabled-save-button reason for a sub-entity modal.

   Returns one of:
   - a vector of localized error strings (missing mandatory fields + per-field
     validation errors) — truncated to 5 with an 'and N more' suffix when
     the list is longer,
   - the localized `:no-changes` string when the form is valid but unchanged,
   - nil when the form is valid and submittable.

   The result is consumed by `dynamic-modal/view`, which renders strings as a
   plain message and vectors as a bulleted list."
  [{:keys [lang-kw template-schema answers-atom field-errors-atom kyc-process changes?]}]
  (let [missing-fields    (resolve-missing-fields lang-kw template-schema answers-atom kyc-process)
        validation-errors (resolve-validation-errors
                           {:lang-kw lang-kw
                            :template-schema template-schema
                            :answers-atom answers-atom
                            :field-errors-atom field-errors-atom
                            :kyc-process kyc-process})
        all-errors        (->> (concat missing-fields validation-errors)
                               (remove cu/blank-value?)
                               vec)
        extra             (- (count all-errors) max-tooltip-items)]
    (cond
      (seq all-errors)
      (cond-> (vec (take max-tooltip-items all-errors))
        (pos? extra)
        (conj (loc/tr :and-x-more-missing-fields [extra] lang-kw)))

      (not changes?) (loc/tr :no-changes nil lang-kw)
      :else          nil)))

(defn- pathname
  []
  (some-> (dg/location)
          (aget "pathname")))

(defn- kyc-process-path?
  [path]
  (and (string? path)
       (string/starts-with? path "/kyc-process")))

(defn- back-until-non-kyc-process!
  []
  (letfn [(step [steps-left]
            (let [current-path (pathname)]
              (cond
                (not (kyc-process-path? current-path))
                nil

                (<= steps-left 0)
                (dg/set-location! "/")

                :else
                (do
                  (dg/history-back)
                  (fu/timeout 100
                              (fn []
                                (if (= current-path (pathname))
                                  (dg/set-location! "/")
                                  (step (dec steps-left)))))))))]
    (step 100)))

(defn exit-button
  [kyc-process lang-kw]
  [fu/tooltip (loc/tr :kyc-exit-tooltip nil lang-kw)
   [button/secondary-hollow
    {:on-click (fn [_]
                 (if (session/is-guest?)
                   (modals/open-modal!
                     confirmation-modal/modal-key
                     {:body           (loc/tr :kyc-exit-confirmation nil lang-kw)
                      :confirm-text   (loc/tr :exit nil lang-kw)
                      :confirm-icon   ""
                      :skip-checkbox? true
                      :on-confirm     (fn [_]
                                        (fu/timeout 1000 account/reset-session)
                                        (let [len (.-length js/window.history)
                                              go (aget js/window.history "go")]
                                          (->> (range len)
                                               (map (fn [_i]
                                                      (.call go js/window.history -1)))
                                               (doall))))})
                   (let [kyc-profile
                         (->>
                           (concat @session/tenant-kyc-profiles
                                   @session/all-kyc-profiles
                                   @session/all-contract-kyc-profiles)
                           (filter (fn [kp]
                                     (or (some #{(:db/id kyc-process)} (:kyc-profile/kyc-processes kp))
                                         (some #{(:db/id kyc-process)} (:tenant-kyc-profile/kyc-processes kp))
                                         (some #{(:db/id kyc-process)} (:contract-kyc-profile/kyc-processes kp)))))
                           (first))]
                     (if kyc-profile
                       (routing/open-entity (:db/id kyc-profile))
                       (back-until-non-kyc-process!)))))}
    (loc/tr :exit nil lang-kw)]])

(defn reminder-button
  [kyc-process lang-kw]
  (when-not (session/is-guest?)
    [button/secondary-hollow
     {:on-click (fn [_]
                  (kyc-process-actions/*->re-invite! {:kyc-process-id (:db/id kyc-process)}
                                                     (fn [_resp]
                                                       (modals/open-modal!
                                                         notification-modal/modal-key
                                                         {:body-text      (loc/tr :re-invitation-sent nil lang-kw)
                                                          :title          (loc/tr :success nil lang-kw)
                                                          :ok-button-text (loc/tr :ok nil lang-kw)}))
                                                     (fn [err]
                                                       (logging/error "Failed to send KYC re-invitation " err))))}
     (loc/tr :send-reminder nil lang-kw)]))
